import { Component, OnInit, AfterViewInit, OnDestroy, HostListener, ViewChild, ElementRef, inject } from '@angular/core';
import { Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError, RouterOutlet } from '@angular/router';
import { Location } from '@angular/common';
import gsap from 'gsap';
import ScrollSmoother from 'gsap/ScrollSmoother';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AnalyticsService } from './core/_services/analytics.service';
import { Subscription } from 'rxjs';
import { NavComponent } from './core/components/nav/nav.component';
import { SiteFooterComponent } from './core/components/site-footer/site-footer.component';
import { CheckForUpdateService } from './core/_services/check-for-update.service';
import { LogUpdateService } from './core/_services/log-update.service';
import { IntroService } from './core/_services/intro.service';
import { isChunkLoadError } from './core/_services/global-error-handler.service';
import { patchScrollSmootherIsInViewport } from './core/utils/gsap-patches';

// Register GSAP plugins once at module load
gsap.registerPlugin(ScrollSmoother, ScrollTrigger);
patchScrollSmootherIsInViewport();

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [NavComponent, RouterOutlet, SiteFooterComponent]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  smoother: ScrollSmoother | undefined;
  public isIntroDone = false;
  sawIntroAnimation = false;
  // Controls rendering of the one-per-session intro animation overlay.
  public showIntro = false;
  private introTimeline: gsap.core.Timeline | undefined;
  @ViewChild('introOverlay') introOverlayEl?: ElementRef<HTMLElement>;
  @ViewChild('introVideo') introVideoEl?: ElementRef<HTMLVideoElement>;
  @ViewChild('introCover') introCoverEl?: ElementRef<HTMLElement>;
  isDone = false;
  location: Location;
  // location is injected by Angular DI
  modules = ['about', 'contact', 'home', 'work'];
  state: string | undefined;
  pageIndex = 0;
  animating: boolean = false;
  title = 'zmbz';
  currentAnimation: gsap.core.Timeline | undefined = undefined;
  private classes: Array<string> = [];
  isCategoryPage = false;
  public categories = [
    'work',
    'people',
    'perspectives'
  ];
  private routerEventsSubscription: Subscription | undefined;

  removeBodyClasses() {
    this.classes.forEach((key: string) => {
      if (key.length > 0) {
        document.body.classList.remove(key + '-page');
      }
    });
  }

  addBodyClasses(path: any) {
    // Extract the first three segments: section, client-name, project-name
    const segments = path.split('?')[0].split('#')[0].split('/');
    this.classes = segments.slice(0, 3); // [section, client-name, project-name]

    this.classes.map((key: string) => {

      if (key.length > 0) {
        document.body.classList.add(key + '-page');
      }

      if (key.length === 0) {
        document.body.classList.add('home-page');
      } else {
        document.body.classList.remove('home-page');
      }

    });
  }

  @HostListener('window:resize')
  onResize() {
    if (this.smoother) {
      ScrollTrigger.refresh();
    }
  }
  // test
  constructor(
    private analyticsService: AnalyticsService,
    private router: Router,
    private deviceService: DeviceDetectorService,
    location: Location,
    private checkForUpdateService: CheckForUpdateService,
    private logUpdateService: LogUpdateService,
    private introService: IntroService,
    // private promptUpdateService: PromptUpdateService
  ) {
    checkForUpdateService = inject(CheckForUpdateService);
    logUpdateService = inject(LogUpdateService);
    // promptUpdateService = inject(PromptUpdateService);
    this.location = location;
    this.isCategoryPage = this.detectCategoryPage(location);
    this.subscribeToRouterEvents();
  }

  ngOnInit(): void {
    // Decide synchronously (before first render) whether the intro overlay
    // should be in the initial DOM so the video can autoplay immediately and
    // cover the nav / footer / page content.
    this.checkCookie();
  }

  ngAfterViewInit() {
    if (this.isDesktop) {
      // Clean up previous instance if it exists
      if (this.smoother) {
        this.smoother.kill();
        this.smoother = undefined;
      }
      this.smoother = ScrollSmoother.create({
        wrapper: '#smooth-wrapper',
        content: '#smooth-content',
        smooth: 1,
        smoothTouch: 0.1,
        normalizeScroll: true
      });
      setTimeout(() => {
        ScrollTrigger.refresh();
      }, 50);
    }
    // Ensure DOM is ready before refreshing ScrollTrigger
    Promise.resolve().then(() => {
      ScrollTrigger.refresh();
    });

    // Kick off the intro animation once the overlay view is available.
    if (this.showIntro) {
      this.runIntroAnimation();
    }
  }

  ngOnDestroy(): void {
    if (this.smoother) {
      this.smoother.kill();
    }
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }
    this.introTimeline?.kill();
  }

  private detectCategoryPage(location: Location): boolean {
    return this.categories.some(category =>
      location.isCurrentPathEqualTo('/news/' + category)
    );
  }

  private recoverFromStaleChunk(targetUrl: string): void {
    const RELOAD_GUARD_KEY = '__zmbz_chunk_reload__';
    let alreadyReloaded = false;
    try {
      alreadyReloaded = sessionStorage.getItem(RELOAD_GUARD_KEY) === '1';
    } catch {
      // storage unavailable (private mode); fall through to a hard navigate
    }

    if (alreadyReloaded) {
      return;
    }

    try {
      sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
    } catch {
      // ignore: still safe to hard-navigate once
    }

    window.location.assign(targetUrl);
  }

  private subscribeToRouterEvents(): void {
    this.routerEventsSubscription = this.router.events.subscribe((event) => {
      if (
        event instanceof NavigationStart ||
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.removeBodyClasses();
        this.addBodyClasses(event.url.substring(1));
        gsap.set('.smooth-wrapper', { autoAlpha: 0 });
      }
      if (event instanceof NavigationError && isChunkLoadError(event.error)) {
        this.recoverFromStaleChunk(event.url);
        return;
      }
      if (event instanceof NavigationStart) {
        gsap.set('.page-wipe-white svg', { scale: 1, opacity: 0 });
        this.leaveAnimation();
        // Set prerenderReady to false when the page is not ready
        (window as any).prerenderReady = false;
      }
      if (event instanceof NavigationEnd) {
        // Implement any logic needed after navigation ends, for example:
        // this.enterAnimation();
        setTimeout(() => {
          window.scrollTo({ top: 0 });
        }, 700);
        setTimeout(() => {
          // Set prerenderReady to true when the page is fully ready
          if (typeof window !== 'undefined' && (window as any).prerenderReady !== undefined) {
            (window as any).prerenderReady = true;
          }
        }, 1000);
      }
    });
  }

  get device(): any {
    return this.deviceService.getDeviceInfo();
  }
  get isDesktop(): boolean {
    return this.deviceService.isDesktop();
  }
  get isMobile(): boolean {
    return this.deviceService.isMobile();
  }
  get isTablet(): boolean {
    return this.deviceService.isTablet();
  }

  onRouteUpdate() {
    if (this.currentAnimation?.reversed()) {
      this.currentAnimation.play();
      return;
    }
    this.currentAnimation = this.scaleImgAnimation();
    this.done();
  }

  scaleImgAnimation() {
    const imgTimeline = gsap.timeline();

    imgTimeline.set('.page-wipe-white svg', { scale: 1, opacity: 0 })
      .to('.page-wipe-white svg', { duration: 0.2, opacity: 1, ease: 'none' })
      .to('.page-wipe-white svg', { duration: 3, scale: 1.5, ease: 'none' }, '<')
      .add(() => this.done(), '>+=1');

    return imgTimeline;
  }

  leaveAnimation() {
    const timeline = gsap.timeline({ defaults: { stagger: 0.03, duration: 0.9, ease: 'expo.inOut' } }),
      loaderPanels = gsap.utils.toArray('.loader__panel');

    timeline.fromTo(loaderPanels, {
      clipPath: 'inset(0% 100% 0% 0%)',
      webkitClipPath: 'inset(0% 100% 0% 0%)'
    }, {
      duration: 1,
      clipPath: 'inset(0% 0% 0% 0%)',
      webkitClipPath: 'inset(0% 0% 0% 0%)',
    }, 0)
      .fromTo(loaderPanels, {
        clipPath: 'inset(0% 0% 0% 0%)',
        webkitClipPath: 'inset(0% 0% 0% 0%)'
      }, {
        duration: 1.4,
        clipPath: 'inset(100% 0% 0% 0%)',
        webkitClipPath: 'inset(100% 0% 0% 0%)',
        stagger: { from: 'end', each: 0.03, ease: 'sine.inOut' }
      }, '>+=0.3')
      .to('.smooth-wrapper', { duration: 0.5, autoAlpha: 1, ease: 'power2.inOut' }, '>-=1.2');

    return timeline;
  }

  done() {
    this.isDone = true;
    this.sawIntroAnimation = false;
    document.body.classList.remove('intro-animation');
    sessionStorage.setItem('introAnimation', 'true');
  }

  setCookie(cname, cvalue, exdays) {
    const d = new Date();
    d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
    const expires = 'expires=' + d.toUTCString();
    document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
  }

  getCookie(cname) {
    const name = cname + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  checkCookie() {
    const cookied = sessionStorage.getItem('introAnimation');
    if (cookied) {
      this.sawIntroAnimation = false;
      this.isIntroDone = true;
      this.showIntro = false;
    } else {
      this.sawIntroAnimation = true;
      this.isIntroDone = false;
      // First visit this session: show the full-screen video intro.
      this.showIntro = true;
    }
  }

  /**
   * Plays the one-per-session intro:
   *   1) The intro video plays full-screen for 6s (covering the whole site).
   *   2) The intro-lockup.svg then acts as a mask over the still-playing
   *      video, scaling down from a massive size to its resting state while
   *      the white background is revealed around it.
   * When finished the overlay fades out and the `introAnimation` session
   * flag is set so it won't play again this session.
   */
  private runIntroAnimation(): void {
    const overlay = this.introOverlayEl?.nativeElement;
    const video = this.introVideoEl?.nativeElement;
    const cover = this.introCoverEl?.nativeElement;

    // If the view isn't available for any reason, don't trap the user behind
    // the overlay — just finish immediately.
    if (!overlay || !video || !cover) {
      this.finishIntro();
      return;
    }

    // Guarantee autoplay + looping (muted is required by browsers for
    // programmatic play; loop keeps the video running behind the lockup the
    // entire time).
    video.muted = true;
    video.loop = true;
    video.play().catch(() => {
      // Autoplay may be blocked; the timeline still runs so the site is revealed.
    });

    const restingSize = this.getIntroRestingSize();
    // Start with the lockup hole larger than the viewport so, when the white
    // cover fades in, almost the entire frame is still the video — the lockup
    // then "falls" toward its resting size.
    const startSize = Math.max(window.innerWidth, window.innerHeight) * 35;
    const maskProxy = { size: startSize };

    // Scale the hole about a pivot INSIDE a letter (not the lockup's center,
    // which sits in the empty gap between text rows). This makes the lockup
    // build out of the video. The lockup still rests perfectly centered — the
    // pivot only governs where the scale emanates from.
    const applyMask = this.buildIntroMaskUpdater(cover, restingSize);
    applyMask(startSize);

    this.introTimeline = gsap.timeline();
    this.introTimeline
      // Phase 2: crossfade the white cover in over the still-playing video so
      // the background eases in through the lockup instead of hard-cutting.
      .to(cover, {
        autoAlpha: 1,
        duration: 1.1,
        ease: 'power1.inOut'
      }, 1.5)
      // Simultaneously scale the lockup hole down to its resting size.
      // `expo.inOut` eases in slowly, then momentum picks up as it falls into
      // frame before settling.
      .to(maskProxy, {
        size: restingSize,
        duration: 3.5,
        ease: 'expo.inOut',
        onUpdate: () => {
          applyMask(maskProxy.size);
        }
      }, 1.5)
      // Hold on the resting lockup, then wipe the overlay up to reveal the site.
      // Collapsing the bottom inset lifts the overlay's bottom edge upward, so
      // the site is revealed from the bottom of the screen up.
      .fromTo(overlay, {
        clipPath: 'inset(0% 0% 0% 0%)'
      }, {
        clipPath: 'inset(0% 0% 100% 0%)',
        duration: 1,
        ease: 'power3.inOut',
        onComplete: () => this.finishIntro()
      }, '>+=4');
  }

  /**
   * Returns a function that, given a mask width, writes the CSS vars driving
   * the lockup hole so it scales about a pivot located INSIDE a letter path
   * while still coming to rest perfectly centered in the viewport.
   *
   * Because CSS `mask-position` with length values positions the mask image's
   * top-left corner, we keep the pivot point fixed on screen at the location it
   * occupies in the centered resting lockup, and offset the top-left corner
   * accordingly for the current size.
   */
  private buildIntroMaskUpdater(cover: HTMLElement, restingSize: number): (width: number) => void {
    // intro-lockup-v2.svg viewBox.
    const VB_W = 772.13;
    const VB_H = 869.59;
    const ratio = VB_H / VB_W;

    // Pivot inside the left vertical stroke of the "N" (second row of the
    // wordmark) — a solid, filled part of a letter, so the scale emanates from
    // the video instead of the transparent gap at the lockup's center.
    const PIVOT_X = 402;
    const PIVOT_Y = 400;
    const fx = PIVOT_X / VB_W;
    const fy = PIVOT_Y / VB_H;

    const W = window.innerWidth;
    const H = window.innerHeight;
    const restingHeight = restingSize * ratio;

    // Where the pivot sits on screen when the lockup rests, centered.
    const pivotScreenX = (W - restingSize) / 2 + fx * restingSize;
    const pivotScreenY = (H - restingHeight) / 2 + fy * restingHeight;

    return (width: number) => {
      const height = width * ratio;
      const left = pivotScreenX - fx * width;
      const top = pivotScreenY - fy * height;
      cover.style.setProperty('--intro-mask-size', `${width}px`);
      cover.style.setProperty('--intro-mask-x', `${left}px`);
      cover.style.setProperty('--intro-mask-y', `${top}px`);
    };
  }

  private getIntroRestingSize(): number {
    // intro-lockup-v2.svg viewBox is 772 x 869 (slightly taller than wide);
    // this value drives the mask WIDTH, height scales to keep aspect ratio.
    // Keep the resting size proportional to the viewport for the final state.
    return Math.round(Math.min(Math.max(window.innerWidth * 0.45, 360), 680));
  }

  private finishIntro(): void {
    this.introTimeline?.kill();
    this.introTimeline = undefined;

    if (this.introVideoEl?.nativeElement) {
      this.introVideoEl.nativeElement.pause();
    }

    this.showIntro = false;
    this.isIntroDone = true;
    this.sawIntroAnimation = false;
    document.body.classList.remove('intro-animation');
    // Persist the flag and notify listeners (e.g. HomeComponent awards loop).
    this.introService.complete();
  }


}
